import React, { useEffect, useMemo, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Select from "./Select"
import Tabs from "aldoo-ra/Tabs"
import { HashtagIcon, ChartBarIcon, TableCellsIcon, PlusIcon, XMarkIcon, TrashIcon, Bars3BottomLeftIcon } from "@heroicons/react/24/outline"
import { SEGMENTS } from "./cards/SegmentsCard"
import useDashboardTexts from "./useDashboardTexts"
import { moneySymbol } from "./format"

const TYPES = [
  { id: "metric", label: "Number", detail: "One figure, compared with the earlier period", icon: HashtagIcon },
  { id: "chart", label: "Chart", detail: "A metric over time, the earlier period beside it", icon: ChartBarIcon },
  { id: "table", label: "Table", detail: "Rows with the columns, sort and filters you choose", icon: TableCellsIcon },
  { id: "segments", label: "Segments", detail: "A metric by time, device, payment or source, each group with its share", icon: Bars3BottomLeftIcon },
]
const SIZES = [
  ["s", "Small"],
  ["m", "Medium"],
  ["l", "Wide"],
]
const GRANULARITIES = [
  ["auto", "Automatic"],
  ["hour", "Hour"],
  ["day", "Day"],
  ["week", "Week"],
  ["month", "Month"],
]
const TEXT_OPS = [
  ["contains", "contains"],
  ["eq", "is"],
]
const NUMBER_OPS = [
  ["gt", ">"],
  ["gte", "≥"],
  ["lt", "<"],
  ["lte", "≤"],
  ["eq", "="],
]
const LIMITS = [5, 10, 25, 50, 100]

const newId = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
const field = "h-9 px-2.5 rounded-lg border border-admin_bordered bg-admin_paper text-sm text-admin_text outline-none focus:border-admin_primary"
const Label = ({ children }) => <span className="text-xs font-semibold uppercase tracking-wide text-admin_text_secondary">{children}</span>

/**
 * A card, new or changed: what it is (a number, a chart, a table), what it
 * shows, and — for a table — its columns, sort, filters and length.
 */
/** The dimensions that split a metric: its own source's (a purchase splits by platform, an order by payment method). */
const dimensionsFor = (catalog, metricId) => {
  const source = catalog?.metrics?.find((m) => m.id === metricId)?.source
  return (catalog?.dimensions || []).filter((d) => !source || !d.source || d.source === source)
}

export default function CardEditor({ isOpen, onClose, card, catalog, onSave, onDelete }) {
  const t = useDashboardTexts()
  const [draft, setDraft] = useState(null)
  // a table's settings, a tab each: which table, its columns, its order, its filters
  const [tableTab, setTableTab] = useState("table")

  useEffect(() => {
    if (!isOpen) return
    setTableTab("table")
    setDraft(card ? JSON.parse(JSON.stringify(card)) : { id: newId(), type: "metric", metric: catalog?.metrics?.[0]?.id, size: "s" })
  }, [isOpen])

  // a metric that is how things stand now (the catalog) has no course over
  // time: a chart does not offer it
  const timeless = (id) => !!catalog?.metrics?.find((m) => m.id === id)?.timeless
  const metricGroups = useMemo(() => {
    const groups = new Map()
    for (const m of catalog?.metrics || []) {
      if (draft?.type === "chart" && m.timeless) continue
      // a metric kept only for the cards saved with it is not offered for new ones
      if (m.hidden && m.id !== draft?.metric) continue
      if (!groups.has(m.group)) groups.set(m.group, [])
      groups.get(m.group).push(m)
    }
    return [...groups.entries()]
  }, [catalog, draft?.type])
  const table = catalog?.tables?.find((x) => x.id === draft?.table)
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }))

  const setType = (type) => {
    if (type === draft.type) return
    if (type === "table") {
      const first = catalog?.tables?.[0]
      return set({ type, table: first?.id, columns: first?.defaultColumns, sort: first?.defaultSort || undefined, filters: [], limit: 10, size: "l", metric: undefined })
    }
    if (type === "segments") return set({ type, metric: draft.metric || catalog?.metrics?.[0]?.id, segment: "weekday", size: "m", table: undefined, columns: undefined, filters: undefined })
    // a chart needs a metric that changes over time
    const metric = type === "chart" && (!draft.metric || timeless(draft.metric)) ? catalog?.metrics?.find((m) => !m.timeless)?.id : draft.metric || catalog?.metrics?.[0]?.id
    set({ type, metric, size: type === "chart" ? "l" : "s", chart: "line", granularity: "auto", table: undefined, columns: undefined, filters: undefined, segment: undefined })
  }
  const setTable = (id) => {
    const t = catalog?.tables?.find((x) => x.id === id)
    set({ table: id, columns: t?.defaultColumns, sort: t?.defaultSort || undefined, filters: [] })
  }
  const toggleColumn = (id) => {
    const on = (draft.columns || []).includes(id)
    const next = on ? draft.columns.filter((c) => c !== id) : table.columns.map((c) => c.id).filter((c) => c === id || (draft.columns || []).includes(c))
    if (next.length) set({ columns: next })
  }
  const setFilter = (i, patch) => set({ filters: draft.filters.map((f, k) => (k === i ? { ...f, ...patch } : f)) })

  const valid = draft && (draft.type === "table" ? !!draft.table && draft.columns?.length : !!draft.metric)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      // the whole screen on a phone, a dialog from 768px
      variant="admin_sheet"
      title={card ? t`Edit card` : t`Add a card`}
      closeOnEscape
      className="w-full md:w-[680px]"
      footerContent={
        <div className="flex items-center justify-between gap-2">
          <div>
            {card && onDelete && (
              <Button variant="admin_secondary" className="text-admin_error border-admin_error/50" onClick={() => onDelete(card)}>
                <span className="flex items-center gap-1"><TrashIcon className="w-4 h-4" />{t`Remove card`}</span>
              </Button>
            )}
          </div>
          {/* closing without saving is the X in the header */}
          <Button variant="admin_primary" className="min-w-[160px]" disabled={!valid} onClick={() => onSave(draft)} data-testid="card-editor-save">
            {card ? t`Save` : t`Add card`}
          </Button>
        </div>
      }
    >
      {draft && (
        <div className="flex flex-col gap-5" data-testid="card-editor">
          {/* the four kinds: a row that slides sideways on a phone, four columns from 768px */}
          <div className="flex md:grid md:grid-cols-4 gap-2 overflow-x-auto md:overflow-visible snap-x snap-mandatory scroll-px-4 -mx-4 px-4 md:mx-0 md:px-0 pb-1 no-scrollbar">
            {TYPES.map((type) => {
              const on = draft.type === type.id
              const Icon = type.icon
              return (
                <button key={type.id} type="button" onClick={() => setType(type.id)} className={`shrink-0 w-[150px] md:w-auto snap-start flex flex-col items-start gap-1 rounded-lg border p-3 text-left ${on ? "border-admin_primary bg-admin_primary/5" : "border-admin_bordered hover:border-admin_primary/50"}`} data-testid={`card-type-${type.id}`}>
                  <Icon className={`w-5 h-5 ${on ? "text-admin_primary" : "text-admin_text_secondary"}`} />
                  <span className="text-sm font-semibold text-admin_text">{t(type.label)}</span>
                  <span className="text-xs text-admin_text_secondary">{t(type.detail)}</span>
                </button>
              )
            })}
          </div>

          {draft.type !== "table" && (
            <label className="flex flex-col gap-1.5">
              <Label>{t`Metric`}</Label>
              <Select
                value={draft.metric || ""}
                options={metricGroups.flatMap(([group, list]) => list.map((m) => ({ value: m.id, label: t(m.label), hint: t(group) })))}
                onChange={(metric) => {
                  // a split or a segment of another source does not split this metric
                  const source = catalog?.metrics?.find((m) => m.id === metric)?.source
                  const fits = (id) => !id || !source || (catalog?.dimensions || []).every((d) => d.id !== id || d.source === source)
                  set({ metric, ...(fits(draft.split) ? {} : { split: undefined }), ...(fits(draft.segment) ? {} : { segment: "weekday" }) })
                }}
                searchEnabled
                testId="card-metric"
              />
              {catalog?.metrics?.find((m) => m.id === draft.metric)?.description && <span className="text-xs text-admin_text_secondary">{t(catalog.metrics.find((m) => m.id === draft.metric).description)}</span>}
            </label>
          )}

          {draft.type === "chart" && (
            <div className="grid grid-cols-2 gap-3">
              {/* one line per value of a dimension: sales per customer, per shipping method … */}
              <label className="flex flex-col gap-1.5 col-span-2">
                <Label>{t`Split by`}</Label>
                <Select
                  value={draft.split || ""}
                  options={[{ value: "", label: t`Nothing — one line` }, ...dimensionsFor(catalog, draft.metric).map((d) => ({ value: d.id, label: t(d.label), hint: t(d.group) }))]}
                  onChange={(split) => set({ split: split || undefined })}
                  searchEnabled
                  testId="card-split"
                />
                {draft.split && <span className="text-xs text-admin_text_secondary">{t`A line for each of the biggest, without the earlier period`}</span>}
              </label>
              {draft.split && (
                <label className="flex flex-col gap-1.5 col-span-2">
                  <Label>{t`Lines`}</Label>
                  <Select value={draft.lines || 5} options={[3, 5, 8].map((n) => [n, t("Top %s", n)])} onChange={(lines) => set({ lines: Number(lines) })} />
                </label>
              )}
              <label className="flex flex-col gap-1.5">
                <Label>{t`Chart`}</Label>
                <Select value={draft.chart || "line"} options={[["line", t`Line`], ["bar", t`Bars`]]} onChange={(chart) => set({ chart })} />
              </label>
              <label className="flex flex-col gap-1.5">
                <Label>{t`Per`}</Label>
                <Select value={draft.granularity || "auto"} options={GRANULARITIES.map(([id, label]) => [id, t(label)])} onChange={(granularity) => set({ granularity })} />
              </label>
            </div>
          )}

          {draft.type === "segments" && (
            <label className="flex flex-col gap-1.5">
              <Label>{t`Segment by`}</Label>
              <Select
                value={draft.segment || "weekday"}
                options={[
                  ...SEGMENTS.map(([value, label]) => ({ value, label: t(label), hint: t`Time` })),
                  ...dimensionsFor(catalog, draft.metric).map((d) => ({ value: d.id, label: t(d.label), hint: t(d.group) })),
                ]}
                onChange={(segment) => set({ segment })}
                searchEnabled
                testId="card-segment"
              />
              <span className="text-xs text-admin_text_secondary">
                {SEGMENTS.some(([id]) => id === (draft.segment || "weekday"))
                  ? t`The date range folded onto it: which hour, weekday or month the figure falls in`
                  : t`The biggest first; past the count below, the rest as one`}
              </span>
            </label>
          )}
          {draft.type === "segments" && !SEGMENTS.some(([id]) => id === (draft.segment || "weekday")) && (
            <label className="flex flex-col gap-1.5">
              <Label>{t`Show`}</Label>
              <Select value={draft.limit || 10} options={[5, 10, 25, 50].map((n) => [n, t("Top %s", n)])} onChange={(limit) => set({ limit: Number(limit) })} />
            </label>
          )}

          {draft.type === "table" && (
            <div className="flex flex-col gap-4">
              <Tabs
                variant="admin"
                size="small"
                value={tableTab}
                onChange={setTableTab}
                tabs={[
                  { value: "table", label: t`Table` },
                  { value: "columns", label: t`Columns`, badge: table ? String((draft.columns || []).length) : null },
                  { value: "sort", label: t`Sort` },
                  { value: "filters", label: t`Filters`, badge: draft.filters?.length ? String(draft.filters.length) : null },
                ]}
              />
              {/* the tabs keep one height, so the dialog does not jump between them */}
              <div className="flex flex-col gap-4 min-h-[260px]" data-testid={`card-tab-${tableTab}`}>
                {tableTab === "table" && (
                  <>
                    <label className="flex flex-col gap-1.5">
                      <Label>{t`Table`}</Label>
                      <Select value={draft.table || ""} options={(catalog?.tables || []).map((x) => [x.id, t(x.label)])} onChange={setTable} testId="card-table" />
                      {table?.description && <span className="text-xs text-admin_text_secondary">{t(table.description)}</span>}
                    </label>

                    {/* the table's own settings; left empty, the table's default holds */}
                    {table?.params?.map((param) => (
                      <label key={param.id} className="flex flex-col gap-1.5">
                        <Label>{t(param.label)}</Label>
                        <input
                          className={`${field} w-[160px]`}
                          type="number"
                          min={param.min}
                          max={param.max}
                          value={draft.params?.[param.id] ?? ""}
                          onChange={(e) => {
                            const next = { ...(draft.params || {}) }
                            if (e.target.value === "") delete next[param.id]
                            else next[param.id] = Number(e.target.value)
                            set({ params: next })
                          }}
                          placeholder={t`Default`}
                          data-testid={`card-param-${param.id}`}
                        />
                        {param.hint && <span className="text-xs text-admin_text_secondary">{t(param.hint)}</span>}
                      </label>
                    ))}

                    <div className="grid grid-cols-3 gap-3">
                      <label className="flex flex-col gap-1.5 col-span-2">
                        <Label>{t`Title`}</Label>
                        <input className={field} value={draft.title || ""} onChange={(e) => set({ title: e.target.value })} placeholder={t(draft.type === "table" ? table?.label : catalog?.metrics?.find((m) => m.id === draft.metric)?.label)} />
                      </label>
                      <label className="flex flex-col gap-1.5">
                        <Label>{t`Size`}</Label>
                        <Select value={draft.size || "s"} options={SIZES.map(([id, label]) => [id, t(label)])} onChange={(size) => set({ size })} />
                      </label>
                    </div>
                  </>
                )}
                {tableTab === "columns" && table && (
                  <>
                    <span className="text-xs text-admin_text_secondary">{t`The columns the card shows`}</span>
                      <div className="flex flex-wrap gap-2" data-testid="card-columns">
                        {table.columns.map((c) => {
                          const on = (draft.columns || []).includes(c.id)
                          return (
                            <button key={c.id} type="button" onClick={() => toggleColumn(c.id)} className={`px-3 h-8 rounded-full border text-xs font-semibold ${on ? "border-admin_primary bg-admin_primary text-white" : "border-admin_bordered text-admin_text hover:border-admin_primary/50"}`}>
                              {t(c.label)}
                            </button>
                          )
                        })}
                      </div>
                  </>
                )}
                {tableTab === "sort" && table && (
                  <div className="grid grid-cols-3 gap-3">
                    <label className="flex flex-col gap-1.5 col-span-2">
                      <Label>{t`Sort by`}</Label>
                      <div className="flex gap-2">
                        <Select className="flex-1" value={draft.sort?.column || ""} options={table.columns.map((c) => [c.id, t(c.label)])} onChange={(column) => set({ sort: { column, direction: draft.sort?.direction || "desc" } })} />
                        <Select className="w-[140px]" value={draft.sort?.direction || "desc"} options={[["desc", t`High to low`], ["asc", t`Low to high`]]} onChange={(direction) => set({ sort: { column: draft.sort?.column || table.columns[0].id, direction } })} />
                      </div>
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <Label>{t`Rows`}</Label>
                      <Select value={draft.limit || 10} options={LIMITS.map((n) => [n, String(n)])} onChange={(limit) => set({ limit: Number(limit) })} />
                    </label>
                  </div>
                )}
                {tableTab === "filters" && table && (
                  <div className="flex flex-col gap-2">
                    <span className="text-xs text-admin_text_secondary">{t`Only the rows that match every filter`}</span>
                    {(draft.filters || []).map((f, i) => {
                      const column = table.columns.find((c) => c.id === f.column) || table.columns[0]
                      const ops = column.type === "number" ? NUMBER_OPS : TEXT_OPS
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <Select
                            className="flex-1"
                            value={f.column}
                            options={table.columns.filter((c) => c.type !== "date").map((c) => [c.id, t(c.label)])}
                            onChange={(id) => {
                              const next = table.columns.find((c) => c.id === id)
                              setFilter(i, { column: id, op: next?.type === "number" ? "gt" : "contains" })
                            }}
                          />
                          <Select className="w-[130px]" value={f.op} options={ops.map(([id, label]) => [id, t(label)])} onChange={(op) => setFilter(i, { op })} />
                          {/* money is typed in whole units of the store currency, kept in cents as the figures are */}
                          <input
                            className={`${field} w-[120px]`}
                            type={column.type === "number" ? "number" : "text"}
                            value={f.value === undefined || f.value === "" ? "" : column.unit === "money" ? Number(f.value) / 100 : f.value}
                            onChange={(e) => setFilter(i, { value: e.target.value === "" ? "" : column.unit === "money" ? Math.round(Number(e.target.value) * 100) : e.target.value })}
                            placeholder={column.unit === "money" ? moneySymbol() : column.unit === "percent" ? "%" : ""}
                          />
                          <button type="button" onClick={() => set({ filters: draft.filters.filter((_, k) => k !== i) })} className="p-1.5 rounded text-admin_text_secondary hover:text-admin_error" title={t`Remove filter`}>
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )
                    })}
                    <button
                      type="button"
                      onClick={() => {
                        const first = table.columns.find((c) => c.type !== "date")
                        set({ filters: [...(draft.filters || []), { column: first.id, op: first.type === "number" ? "gt" : "contains", value: "" }] })
                      }}
                      className="self-start inline-flex items-center gap-1 text-sm font-semibold text-admin_primary hover:underline"
                    >
                      <PlusIcon className="w-4 h-4" />{t`Add filter`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {draft.type !== "table" && (
            <div className="grid grid-cols-3 gap-3">
              <label className="flex flex-col gap-1.5 col-span-2">
                <Label>{t`Title`}</Label>
                <input className={field} value={draft.title || ""} onChange={(e) => set({ title: e.target.value })} placeholder={t(draft.type === "table" ? table?.label : catalog?.metrics?.find((m) => m.id === draft.metric)?.label)} />
              </label>
              <label className="flex flex-col gap-1.5">
                <Label>{t`Size`}</Label>
                <Select value={draft.size || "s"} options={SIZES.map(([id, label]) => [id, t(label)])} onChange={(size) => set({ size })} />
              </label>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
